/** @jsx jsx */
import { jsx } from "theme-ui"
import PropTypes from "prop-types"
import { useContext, useEffect, useMemo, useRef, useState } from "react"

import { ab } from "../../theme"
import CaptchaContext from "../../context/captchaContext"

const Captcha = ({
  label,
  name,
  type = "text",
  value, // optional initial value
  placeholder,
  required,
  wrongAnswer,
  children,
}) => {
  const { setCaptcha } = useContext(CaptchaContext)
  const refInput = useRef(null)

  const operators = useMemo(() => ["+", "-", "*"], [])

  const [wrongAnswerText, setWrongAnswerText] = useState("")
  const [randomNum1, setRandomNum1] = useState(() =>
    Math.ceil(Math.random() * 10)
  )
  const [randomNum2, setRandomNum2] = useState(() =>
    Math.ceil(Math.random() * 10)
  )
  const [operatorIndex, setOperatorIndex] = useState(() =>
    Math.floor(Math.random() * operators.length)
  )

  const operator = operators[operatorIndex]

  const randomQuestion = useMemo(() => {
    return `${randomNum1}${operator}${randomNum2}`
  }, [randomNum1, randomNum2, operator])

  const correctAnswer = useMemo(() => {
    if (operator === "+") return randomNum1 + randomNum2
    if (operator === "-") return randomNum1 - randomNum2
    return randomNum1 * randomNum2
  }, [randomNum1, randomNum2, operator])

  useEffect(() => {
    const inputEl = refInput.current
    const form = inputEl?.closest("form")
    if (!form || !inputEl) return

    const checkAnswer = (e) => {
      const inputAnswer = Number(inputEl.value)
      const isCorrect = correctAnswer === inputAnswer

      if (isCorrect) {
        setCaptcha(true)
        setWrongAnswerText("")
      } else {
        setCaptcha(false)
        setWrongAnswerText(wrongAnswer || "Wrong answer")
        e.preventDefault() // BLOCK submit
        e.stopPropagation()
      }
    }

    // use capture phase so we intercept before other submit handlers
    form.addEventListener("submit", checkAnswer, true)

    return () => {
      form.removeEventListener("submit", checkAnswer, true)
    }
  }, [correctAnswer, wrongAnswer, setCaptcha])

  return (
    <div
      className="captchaDiv"
      sx={{
        py: 3,
      }}
    >
      <label htmlFor={name}>
        {children || label}
        {required ? " * " : ""}
      </label>

      <div
        sx={{
          display: ab({ _: "flex", md: "flex" }),
        }}
      >
        <div
          sx={{
            px: 4,
            my: ab({ _: 3, md: 2 }),
            fontSize: 24,
            flex: 3,
            pt: 3,
            textAlign: "center",
          }}
        >
          {randomQuestion}
          <span
            sx={{
              pl: "20px",
            }}
          >
            =
          </span>
        </div>

        <input
          ref={refInput}
          sx={{
            variant: "form.formField",
            border: 0,
            px: 2,
            my: 2,
            color: "text",
            flex: 3,
            width: "100%",
          }}
          id={name}
          name={name}
          type={type}
          defaultValue={value} // uncontrolled input (recommended here)
          placeholder={placeholder}
          required={required ? true : undefined}
          aria-invalid={wrongAnswerText ? "true" : "false"}
        />
      </div>

      <p
        sx={{
          textAlign: "right",
          p: 0,
          mt: 0,
          pr: 3,
          color: "#ff3454",
        }}
      >
        {wrongAnswerText}
      </p>
    </div>
  )
}

Captcha.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  type: PropTypes.string,
  value: PropTypes.string,
  placeholder: PropTypes.string.isRequired,
  required: PropTypes.bool,
  wrongAnswer: PropTypes.string,
  children: PropTypes.node,
}

export default Captcha
